/**
 * Pure functions of the `boggle` type: the definition's starting point, the progress blob,
 * the submission, and how a server verdict folds back into the progress. No component here,
 * so `module.ts` stays light in the eager bundle.
 */
import type { Verdict } from "../types";
import { normalizeWord, wordIssue } from "./tray";
import {
  DEFAULT_MIN_LENGTH,
  MAX_WORDS,
  minLengthOf,
  type BoggleDefinition,
  type BoggleProgress,
  type BoggleSubmission,
  type WordIssue,
} from "./types";

const STARTER_GRID = [
  ["T", "R", "A", "P"],
  ["E", "S", "I", "N"],
  ["L", "O", "D", "E"],
  ["M", "A", "R", "S"],
];

/** The server's default rules (`DEFAULT_RULES` in type.py), for a given shortest length. */
export function defaultRules(min: number): Record<"en" | "fr", string> {
  return {
    en:
      "Find words by joining letters that touch — across, up, down or diagonally — without " +
      `using the same die twice in a word. Words need at least ${min} letters (Qu counts as ` +
      "two). Words of 3 or 4 letters score 1 point, 5 letters 2, 6 letters 3, 7 letters 5, " +
      "8 or more 11. Build your list, then submit it once: you are ranked on the points of " +
      "the words in the dictionary. A list with no valid word does not count.",
    fr:
      "Formez des mots en reliant des lettres qui se touchent — horizontalement, " +
      "verticalement ou en diagonale — sans utiliser deux fois le même dé dans un mot. Un " +
      `mot compte au moins ${min} lettres (Qu en vaut deux). Un mot de 3 ou 4 lettres vaut ` +
      "1 point, de 5 lettres 2, de 6 lettres 3, de 7 lettres 5, de 8 ou plus 11. Composez " +
      "votre liste, puis soumettez-la une seule fois : vous êtes classé sur les points des " +
      "mots du dictionnaire. Une liste sans aucun mot valable ne compte pas.",
  };
}

/** Same as the server's `empty_definition()`. */
export function emptyDefinition(): BoggleDefinition {
  return {
    puzzleType: "boggle",
    typeVersion: 1,
    language: "en",
    size: 4,
    grid: STARTER_GRID.map((row) => [...row]),
    minLength: DEFAULT_MIN_LENGTH,
    rulesText: defaultRules(DEFAULT_MIN_LENGTH),
  };
}

export function emptyProgress(_definition: BoggleDefinition): BoggleProgress {
  return { version: 1, words: [] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const WORD = /^[A-Z]{1,32}$/;

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: BoggleDefinition, raw: unknown): BoggleProgress {
  if (!isRecord(raw) || !Array.isArray(raw.words)) return emptyProgress(definition);
  const words: string[] = [];
  for (const item of raw.words) {
    if (typeof item !== "string") continue;
    const word = normalizeWord(item);
    if (WORD.test(word) && !words.includes(word)) words.push(word);
  }
  const rejected: Record<string, string> = {};
  if (isRecord(raw.rejected)) {
    for (const [word, code] of Object.entries(raw.rejected)) {
      if (typeof code === "string" && words.includes(word)) rejected[word] = code;
    }
  }
  return {
    version: 1,
    words: words.slice(0, MAX_WORDS),
    ...(Object.keys(rejected).length > 0 ? { rejected } : {}),
  };
}

export function serializeSubmission(
  _definition: BoggleDefinition,
  progress: BoggleProgress,
): BoggleSubmission {
  return { words: [...progress.words] };
}

export type AddResult =
  | { ok: true; progress: BoggleProgress; word: string }
  | { ok: false; word: string; reason: WordIssue | "duplicate" | "empty" | "full" };

/** Adds a word to the list if it keeps the visible rules; the dictionary is the server's. */
export function addWord(
  definition: BoggleDefinition,
  progress: BoggleProgress,
  raw: string,
): AddResult {
  const word = normalizeWord(raw);
  if (!word) return { ok: false, word, reason: "empty" };
  if (progress.words.includes(word)) return { ok: false, word, reason: "duplicate" };
  const issue = wordIssue(definition.grid, minLengthOf(definition), word);
  if (issue) return { ok: false, word, reason: issue };
  if (progress.words.length >= MAX_WORDS) return { ok: false, word, reason: "full" };
  return { ok: true, word, progress: { ...progress, words: [...progress.words, word] } };
}

export function removeWord(progress: BoggleProgress, word: string): BoggleProgress {
  const words = progress.words.filter((w) => w !== word);
  if (!progress.rejected || !(word in progress.rejected)) return { ...progress, words };
  const rest = { ...progress.rejected };
  delete rest[word];
  return Object.keys(rest).length > 0
    ? { ...progress, words, rejected: rest }
    : { version: 1, words };
}

/**
 * Folds the server's verdict into the progress: every submitted word loses its old mark,
 * then each refused word gets the reason the server gave. The feedback names the player's
 * own words only, so nothing here can come from a word they did not find.
 */
export function applyVerdict(
  _definition: BoggleDefinition,
  progress: BoggleProgress,
  verdict: Verdict<BoggleSubmission>,
): BoggleProgress {
  const submitted = new Set(verdict.submission.words.map(normalizeWord));
  const rejected: Record<string, string> = {};
  for (const [word, code] of Object.entries(progress.rejected ?? {})) {
    if (!submitted.has(word)) rejected[word] = code;
  }
  for (const item of verdict.feedback) {
    const word = item.params?.word;
    if (typeof word !== "string" || item.code === "duplicate_word") continue;
    if (progress.words.includes(word)) rejected[word] = item.code;
  }
  const next: BoggleProgress = { version: 1, words: progress.words };
  return Object.keys(rejected).length > 0 ? { ...next, rejected } : next;
}

/** The points the list would score if every word is in the dictionary, marks excluded. */
export function tentativeScore(progress: BoggleProgress, pointsOf: (w: string) => number): number {
  return progress.words
    .filter((word) => !progress.rejected?.[word])
    .reduce((sum, word) => sum + pointsOf(word), 0);
}
